import React from "react";
import { Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

export interface TimelineStep {
  id: string;
  title: string;
  status: "completed" | "active" | "inactive";
}

interface VerticalTimelineProps {
  steps: TimelineStep[];
  className?: string;
}

const VerticalTimeline: React.FC<VerticalTimelineProps> = ({
  steps,
  className = "",
}) => {
  /**
   * Renders professional timeline icons based on step status
   * - Completed: Green background with white checkmark
   * - Active: Green background with white number and glow effect
   * - Inactive: Light gray background with gray number
   */
  const renderIcon = (step: TimelineStep, index: number) => {
    switch (step.status) {
      case "active":
        return (
          <div className="timeline-icon-content active-icon">
            <span className="step-number">{index + 1}</span>
          </div>
        );
      case "inactive":
        return (
          <div className="timeline-icon-content inactive-icon">
            <span className="step-number">{index + 1}</span>
          </div>
        );
      default:
        return (
          <div className="timeline-icon-content inactive-icon">
            <span className="step-number">{index + 1}</span>
          </div>
        );
    }
  };

  return (
    <Box className={`vertical-timeline ${className}`}>
      <div className="timeline-container">
        {steps.map((step, index) => (
          <div key={step.id} className="timeline-item">
            <div className={`timeline-icon ${step.status}`}>
              {renderIcon(step, index)}
            </div>
            <div className="timeline-content">
              <h3 className="timeline-title">{step.title}</h3>
            </div>
            {index < steps.length - 1 && <div className="timeline-connector" />}
          </div>
        ))}
      </div>
    </Box>
  );
};

/**
 * Memoized VerticalTimeline component to prevent unnecessary re-renders
 * Only re-renders when the steps array actually changes
 */
export default React.memo(VerticalTimeline, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)
  if (prevProps.className !== nextProps.className) return false;
  if (prevProps.steps.length !== nextProps.steps.length) return false;

  // Deep comparison of steps to detect actual changes
  return prevProps.steps.every((step, index) => {
    const nextStep = nextProps.steps[index];
    return (
      step.id === nextStep.id &&
      step.title === nextStep.title &&
      step.status === nextStep.status
    );
  });
});
